<template>
  <div>
    <div class="betslip betslip-body-surface" ref="betslipContainer">
      <div class="betslip-header header-bar-surface">
        <h3>Your BetSlip</h3>
        <span class="bet-count">{{ bets.length }}</span>
      </div>
      <div v-if="isAuthenticated && showBookABetModule" class="betslip-load-booking">
        <div class="booking-input-container">
          <input
            v-model="bookingCodeInput"
            type="text"
            class="booking-code-input"
            :placeholder="$t('betslip.enterBookingCode')"
            @keyup.enter="loadBookedBet"
          />
          <button
            type="button"
            class="booking-search-btn"
            :disabled="!bookingCodeInput.trim() || isLoadingBooking"
            @click="loadBookedBet"
          >
            {{ isLoadingBooking ? $t('betslip.loading') : $t('betslip.load') }}
          </button>
        </div>
        <div v-if="bookingMessage" class="booking-message success">{{ bookingMessage }}</div>
        <div v-if="bookingErrorMessage" class="booking-message error">{{ bookingErrorMessage }}</div>
      </div>
      <ToastComponent
        v-if="toast"
        :title="toast.title"
        :message="toast.message"
        :type="toast.type"
        :duration="toast.duration"
      />
      <div v-if="bets.length === 0">

        <div v-if="bookBetSuccessData" class="booked-bet-success-inline">
          <h4 class="booked-success-title">{{ $t('betslip.betBookedSuccess') }}</h4>
          <div class="booking-info">
            <div class="booking-row"><label>{{ $t('betslip.bookingCode') }}:</label> <span class="code-value">{{ bookBetSuccessData.booking_code }}</span></div>
            <div class="booking-row"><label>{{ $t('betslip.totalAmount') }}:</label> <span>{{ formatCurrency(bookBetSuccessData.total_bet_amount || 0, unref(displayCurrency)) }}</span></div>
            <div class="booking-row"><label>{{ $t('betslip.expires') }}:</label> <span>{{ formatDate(bookBetSuccessData.expires_at || '') }}</span></div>
          </div>
          <div v-if="bookBetSuccessData.barcode" class="barcode-box">
            <img :src="`data:image/png;base64,${bookBetSuccessData.barcode}`" alt="Booking barcode" class="barcode-img" />
            <p class="barcode-hint">{{ $t('betslip.scanOrUseCode') }}</p>
          </div>
          <div class="booking-actions">
            <button type="button" class="download-barcode-btn" @click="downloadBookedBarcode">{{ $t('betslip.downloadReceipt') }}</button>
            <button type="button" class="done-btn" @click="bookBetSuccessData = null">{{ $t('common.done') }}</button>
          </div>
        </div>
        <div v-else-if="showSuccess" class="success-message">
          <div class="success-content">
            <span class="success-icon">🎉</span>
            <div class="success-text">
              <h3>{{ $t('betslip.betPlacedSuccess') }}</h3>
              <p>{{ $t('betslip.goodLuck') }}</p>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <p>{{ $t('betslip.empty') }}</p>
        </div>
        <div class="view-last-bet" v-if="isAuthenticated && !bookBetSuccessData">
          <button
            v-if="lastBetReference"
            class="view-bet-button"
            :disabled="isLoadingLastBet"
            @click="viewLastBet"
          >
            {{ isLoadingLastBet ? $t('betslip.loadingLastBet') : $t('betslip.viewLastBet') }}
          </button>
          <button class="view-bet-button" @click="viewWagers">{{ $t('betslip.viewWagers') }}</button>
        </div>
      </div>
      <template v-else>
        <div class="betslip-content">
          <div v-for="(bet, index) in bets" :key="index" class="bet-item" :class="{ 'expired-bet': showExpiredBetOptions && hasExpiredDraws(bet) }">
            <div class="bet-header">
              <div class="bet-title">
                <span class="game-name">{{ bet.game_name }}</span>
                <span class="game-date">{{
                  formatDate(bet.draws?.[0]?.draw_date)
                }}</span>
                <button class="close-button" @click="removeBetAtIndex(index)">
                  ×
                </button>
              </div>
            </div>
            <div class="bet-numbers">
              <div class="numbers-label">
                <div class="market-info">{{ bet.market_name }}</div>
                <div class="market-desc" v-if="!bet.numbers || !hasSelectedNumbers(bet.numbers)">
                  {{
                    formatMarketDescription(
                      bet.market_description || "",
                      bet.specifier || ""
                    )
                  }}
                </div>
                <div class="bet-label">
                  {{
                    formatBetLabel(bet.label || "", {
                      specifier_value: bet.specifier || undefined,
                    })
                  }}
                </div>
              </div>
              <div v-if="bet.numbers && hasSelectedNumbers(bet.numbers)">
                <div class="numbers-label">Numbers Chosen:</div>
                <div class="number-circles">
                  <!-- Main numbers -->
                  <span
                    v-for="number in Array.isArray(bet.numbers) &&
                    Array.isArray(bet.numbers[0])
                      ? bet.numbers[0]
                      : []"
                    :key="'main-' + number"
                    class="number-circle"
                  >
                    {{ number }}
                  </span>
                  <!-- Bonus numbers -->
                  <template
                    v-if="
                      Array.isArray(bet.numbers) &&
                      Array.isArray(bet.numbers[1]) &&
                      bet.numbers[1].length
                    "
                  >
                    <span
                      v-if="
                        Array.isArray(bet.numbers[0]) && bet.numbers[0].length
                      "
                      class="plus-sign"
                      >+</span
                    >
                    <span
                      v-for="bonus in bet.numbers[1]"
                      :key="'bonus-' + bonus"
                      class="number-circle bonus"
                    >
                      {{ bonus }}
                    </span>
                  </template>
                </div>
              </div>
            </div>
            <div class="bet-details">
              <div class="bet-controls">
                <div class="stake-draw-grid">
                  <!-- Draw Section -->
                  <div class="draw-section">
                    <label class="field-label">Draw:</label>
                    <div class="selected-draws-display">
                      <div v-if="Array.isArray(bet.draws) && bet.draws.length > 0" class="draws-list">
                        <span
                          v-for="(draw, drawIdx) in bet.draws"
                          :key="drawIdx"
                          class="draw-badge"
                        >
                          {{ formatDate(draw?.draw_date) }}
                        </span>
                      </div>
                      <span v-else class="no-draws-selected">No draws selected</span>
                    </div>
                  </div>

                  <!-- Stake Section -->
                  <div class="stake-section">
                    <label class="field-label">Stake:</label>
                    <div class="stake-input-container">
                      <span class="currency-symbol">{{
                        unref(displayCurrency) || unref(currencySymbol)
                      }}</span>
                      <input
                        type="number"
                        class="stake-input"
                        :value="
                          localStakeValues[index] || bet.stake || betDefault
                        "
                        :min="betMin"
                        :max="betMax"
                        :step="allowDecimals ? 0.01 : 1"
                        :placeholder="`${betMin}`"
                        @input="(event) => handleStakeChange(event, index)"
                        @blur="(event) => handleStakeBlur(event, index)"
                        @focus="(event) => handleStakeFocus(event, index)"
                        @keydown="(event) => handleStakeKeydown(event)"
                      />
                    </div>
                  </div>
                </div>
              </div>


              <div class="bet-row">
                <span>Bet Type:</span>
                <span>{{ bet.betType }}</span>
              </div>
              <div class="bet-row">
                <span>Odds:</span>
                <span>{{ formatOdds(bet.odds) }}</span>
              </div>
              <div class="bet-row">
                <span>To Win:</span>
                <span class="win-amount"
                  >{{ formatCurrency(
                      (bet.stake || 0) *
                        (bet.odds || 0) *
                        (Array.isArray(bet.draws) ? bet.draws.length : 1) *
                        (bet.numBets || 1),
                      unref(displayCurrency) || unref(currencySymbol)
                    )
                  }}</span
                >
              </div>
            </div>
          </div>

          <div class="betslip-footer">
            <div class="clear-button-wrapper">
              <button class="clear-button" @click="() => clearBetslip()">
                Clear all
                <span class="trash-icon">🗑️</span>
              </button>
              <button
                v-if="showBookABetModule"
                type="button"
                class="book-bet-button"
                :disabled="isProcessingBookBet"
                @click="handleBookBet"
              >
                {{ isProcessingBookBet ? 'Booking...' : 'Book a Bet' }}
              </button>
            </div>
            <div class="total-bet">
              <span>Total Bet Amount:</span>
              <span class="amount"
                >{{ formatCurrency(totalStake, unref(displayCurrency)) }}</span
              >
            </div>
            <div class="total-bet">
              <span>Total Potential Returns:</span>
              <span class="amount win-amount"
                >{{ formatCurrency(totalReturns, unref(displayCurrency)) }}</span
              >
            </div>
            <!-- Expired bet options -->
            <div v-if="showExpiredBetOptions" class="expired-bet-options">
              <div class="expired-message">
                <span class="warning-icon">⚠️</span>
                <span class="warning-text">{{ $t('betslip.expiredBets') }}</span>
              </div>
              <div class="expired-actions">
                <button 
                  class="remove-expired-btn" 
                  @click="removeExpiredBets"
                >
                  Remove
                </button>
                <button 
                  v-if="hasAnyGameWithNextDraw"
                  class="move-to-next-btn" 
                  @click="moveToNextDraw"
                >
                  Move to Next Draw
                </button>
              </div>
            </div>

            <!-- Inline error message above BET NOW button -->
            <div v-if="inlineError" class="inline-error-message">
              <span class="error-icon">❌</span>
              <span class="error-text">{{ inlineError }}</span>
            </div>
            <button
              class="place-bet"
              :class="{ processing: isProcessingBet, unauthenticated: !isAuthenticated }"
              @click="handleBetButtonClick"
              :disabled="isAuthenticated ? (isProcessingBet || isEmpty || showExpiredBetOptions) : false"
            >
              {{ !isAuthenticated ? $t('common.pleaseLogin') : (isProcessingBet ? $t('common.pleaseWait') : $t('common.betNow')) }}
            </button>
            <!-- Close button for mobile only -->
            <button
              class="close-betslip-button"
              @click="closeBetslip"
            >
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <BaseModal
      :is-open="isLastBetModalOpen"
      :title="$t('betslip.lastBetDetails')"
      full-screen
      @close="isLastBetModalOpen = false"
    >
      <TransactionDetails
        v-if="transactionsStore.currentTransaction"
        :transaction="transactionsStore.currentTransaction"
      />
    </BaseModal>

  </div>
</template>

<script setup lang="ts">
import {
  computed,
  watch,
  ref,
  defineEmits,
  nextTick,
  onMounted,
  onUnmounted,
  unref,
} from "vue";
import { storeToRefs } from "pinia";
import { useBetslipStore } from "../stores/betslip";
import type { BetslipItem } from "../stores/betslip";
import ToastComponent from "./ToastComponent.vue";
import { useToast } from "../composables/useToast";
import { useGameStore } from "../stores/game";
import { useTransactionsStore } from "../stores/transactions";
import { useAuthStore } from "../stores/auth";
import { useRouter, useRoute } from "vue-router";
import { formatCurrency, formatOdds } from '../utils/currency';
import { useBetSettings } from "../composables/useBetSettings";
import { iframeCommunication } from "../utils/iframeCommunication";
import BaseModal from "./BaseModal.vue";
import TransactionDetails from "./wagers/TransactionDetails.vue";

const emit = defineEmits(["close", "selectDraws", "drawsChanged", "clearedAfterSubmission"]);

const betslipContainer = ref<HTMLElement | null>(null);

const gameStore = useGameStore();
const transactionsStore = useTransactionsStore();
const betslipStore = useBetslipStore();
const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);
const { removeBet, clearBetslip: storeClearBetslip, updateLastStake } = betslipStore;
const betslip = computed(() => betslipStore.betslip);
const { toast, showToast } = useToast();
const isProcessingBet = ref(false);
const showSuccess = ref(false);
const isUpdatingFromProps = ref(false);
const isClearingBetslip = ref(false);
const localStakeValues = ref<{ [key: number]: string }>({});
const showExpiredBetOptions = ref(false);
const expiredBets = ref<Array<{ betIndex: number; drawIndex: number; draw: { draw_date: string; cutoff: string; event_id: number } }>>([]);
const bookingCodeInput = ref('');
const isLoadingBooking = ref(false);
const bookingMessage = ref('');
const bookingErrorMessage = ref('');
const currentBookingCode = ref<string | null>(null);
const isProcessingBookBet = ref(false);
const bookBetSuccessData = ref<{
  booking_code: string;
  barcode?: string;
  expires_at?: string;
  total_bet_amount?: number;
  bets?: BetslipItem[];
} | null>(null);

const showBookABetModule = computed(() => {
  const modules = gameStore.getBetSettings?.enabled_modules;
  if (!Array.isArray(modules)) return false;
  return modules.includes('book_a_bet');
});

// Wrapper function for clearing betslip
const clearBetslip = (resetSuccess = true) => {
  isClearingBetslip.value = true;
  if (resetSuccess) {
    showSuccess.value = false; // Reset success message when manually clearing
  }
  storeClearBetslip();
  // Reset flag after a short delay
  setTimeout(() => {
    isClearingBetslip.value = false;
  }, 100);
};
const cleanupTimer = ref<number | null>(null);
const inlineError = ref<string | null>(null);
const isMobile = ref(false);
const openDrawDropdownIndex = ref<number | null>(null);
const clickOutsideHandler = ref<((event: MouseEvent) => void) | null>(null);
const isLastBetModalOpen = ref(false);
const isLoadingLastBet = ref(false);

// Use bet settings composable
const { betMin, betMax, betDefault, currencySymbol, allowDecimals } =
  useBetSettings();

const bets = computed(() => {
  return betslip.value || [];
});

// Wrapper function to handle index for removeBet
const removeBetAtIndex = (displayIndex: number) => {
  removeBet(displayIndex);
};

// Close betslip function for mobile
const closeBetslip = () => {
  emit('close');
};

const isEmpty = computed(() => {
  if (!bets.value || bets.value.length === 0) return true;
  return bets.value.some(
    (bet) =>
      !Array.isArray(bet.draws) ||
      bet.draws.length === 0 ||
      !bet.stake ||
      bet.stake <= 0
  );
});

const totalStake = computed(() => {
  if (!bets.value) return 0;
  return bets.value.reduce((total, bet) => {
    const drawCount = Array.isArray(bet.draws) ? bet.draws.length : 1;
    const numBets =
      typeof bet.numBets === "number" && bet.numBets > 0 ? bet.numBets : 1;
    return total + (bet.stake || 0) * numBets * drawCount;
  }, 0);
});

const totalReturns = computed(() => {
  if (!bets.value) return 0;
  return bets.value.reduce((total, bet) => {
    const drawCount = Array.isArray(bet.draws) ? bet.draws.length : 1;
    const numBets =
      typeof bet.numBets === "number" && bet.numBets > 0 ? bet.numBets : 1;
    const odds = typeof bet.odds === "number" ? bet.odds : 0;
    return total + (bet.stake || 0) * numBets * drawCount * odds;
  }, 0);
});

// Get currency symbol from first bet or fall back to game settings
const displayCurrency = computed((): string => {
  if (bets.value && bets.value.length > 0) {
    const symbol = bets.value[0].currency_symbol;
    if (symbol) return symbol;
  }
  return unref(currencySymbol) || '';
});

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(max-width: 768px)').matches;

// Function to scroll betslip to bottom (desktop: show newly added line)
const scrollToBottom = () => {
  nextTick(() => {
    if (betslipContainer.value) {
      // Find the betslip-content element within the betslip container
      const betslipContent = betslipContainer.value.querySelector('.betslip-content') as HTMLElement;
      if (betslipContent) {
        betslipContent.scrollTo({
          top: betslipContent.scrollHeight,
          behavior: 'smooth'
        });
      } else {
        // Fallback to scrolling the main container
        betslipContainer.value.scrollTo({
          top: betslipContainer.value.scrollHeight,
          behavior: 'smooth'
        });
      }
    }
  });
};

/** Mobile: `.betslip` is the scroll host (overflow-y:auto); `.betslip-content` is visible overflow. */
const scrollBetslipRootsToTop = () => {
  const root = betslipContainer.value;
  if (!root) return;
  root.scrollTop = 0;
  const outerPanel = root.closest('.mobile-betslip-content') as HTMLElement | null;
  if (outerPanel) outerPanel.scrollTop = 0;
  const innerContent = root.querySelector('.betslip-content') as HTMLElement | null;
  if (innerContent) innerContent.scrollTop = 0;
};

/** Full-screen mobile panel: show header and top of slip after add. */
const scrollToTop = () => {
  const run = () => scrollBetslipRootsToTop();
  nextTick(() => {
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  });
};

const revealSuccessState = () => {
  nextTick(() => {
    scrollToTop();
    iframeCommunication.sendScrollToTop();
    iframeCommunication.sendImmediateHeightUpdate();

    [80, 220, 450].forEach((delay) => {
      window.setTimeout(() => {
        scrollToTop();
        iframeCommunication.sendScrollToTop();
        iframeCommunication.sendImmediateHeightUpdate();
      }, delay);
    });
  });
};


// Watch for changes in the betslip store to ensure stakes are set
watch(
  betslip,
  async (newBets: BetslipItem[]) => {
    if (isUpdatingFromProps.value) return; // Prevent recursion
    if (isClearingBetslip.value) return; // Prevent interference during clearing
    if (!newBets || !Array.isArray(newBets)) return; // Add null check
    
    // If betslip is empty (cleared), don't process it
    if (newBets.length === 0) return;

    await nextTick();
    // Use last stake if available, otherwise fall back to betDefault
    const lastStake = betslipStore.getLastStake || betDefault.value;
    const updatedBets = newBets.map((bet: BetslipItem) => ({
      ...bet,
      stake: bet.stake || lastStake,
    }));

    if (JSON.stringify(updatedBets) !== JSON.stringify(newBets)) {
      betslipStore.betslip = updatedBets;
      betslipStore.saveToStorage();
    }
  },
  { immediate: true, deep: true }
);

// Watch for changes in bets: mobile full-screen betslip → top; desktop → bottom
watch(
  () => bets.value.length,
  (newLength, oldLength) => {
    if (newLength > oldLength && newLength > 0) {
      if (isMobileViewport()) {
        scrollToTop();
      } else {
        scrollToBottom();
      }
    }
  }
);


const handleStakeKeydown = (event: KeyboardEvent) => {
  // Prevent comma input
  if (event.key === ',') {
    event.preventDefault();
  }
};

const handleStakeFocus = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  // Store the current betslip value as the starting point
  const currentBet = betslip.value?.[index];
  localStakeValues.value[index] = String(currentBet?.stake || betDefault);
};

const handleStakeChange = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  // Remove any commas from the input
  value = value.replace(/,/g, '');

  // Store the cleaned input value locally
  localStakeValues.value[index] = value;

  // Allow empty input for user to type
  if (value === "") {
    return;
  }

  // Parse the value as float
  let stake = parseFloat(value);

  // If it's not a valid number, don't update betslip but keep local value
  if (isNaN(stake)) {
    return;
  }

  // Validate minimum stake immediately
  if (stake < betMin.value) {
    // Don't update betslip if below minimum, but keep local value for user to continue typing
    return;
  }

  // Update betslip immediately for calculations
  updateBetStake(index, stake, false);
  
  // Save the stake as the last used stake
  updateLastStake(stake);
};

const handleStakeBlur = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  let value = target.value.trim();

  // Remove any commas from the input
  value = value.replace(/,/g, '');

  // Clear local value to show betslip value
  delete localStakeValues.value[index];

  // Handle empty input
  if (value === "") {
    updateBetStake(index, betMin.value, true);
    return;
  }

  // Parse the value as float
  let stake = parseFloat(value);

  // If it's not a valid number, set to minimum
  if (isNaN(stake)) {
    updateBetStake(index, betMin.value, true);
    return;
  }

  // Update the betslip with the final value
  updateBetStake(index, stake, true);
  
  // Save the stake as the last used stake
  updateLastStake(stake);
};

const hasSelectedNumbers = (numbers: number[][]) => {
  if (!Array.isArray(numbers)) return false;
  return numbers.some(
    (numberArray) => Array.isArray(numberArray) && numberArray.length > 0
  );
};


const updateBetStake = (
  displayIndex: number,
  value: string | number,
  isBlur = false
) => {
  if (!betslip.value) return;

  // Display index now matches actual betslip index
  const actualIndex = displayIndex;

  let stakeValue = Number(value);

  // Handle invalid values
  if (isNaN(stakeValue)) {
    return; // Don't update if invalid
  }

  // Handle empty or zero values
  if (stakeValue <= 0) {
    stakeValue = 0;
  }

  // Validate against bet settings
  if (stakeValue < betMin.value) {
    stakeValue = betMin.value;
  }

  if (stakeValue > betMax.value) {
    stakeValue = betMax.value;
  }

  // Handle decimals based on allowDecimals setting
  if (!allowDecimals.value) {
    stakeValue = Math.floor(stakeValue);
  } else {
    // Only round to 2 decimal places on blur, not during typing
    if (isBlur) {
      // Use parseFloat with toFixed to avoid floating point precision issues
      stakeValue = parseFloat(stakeValue.toFixed(2));
    }
    // During typing, preserve the exact decimal value for calculations
  }

  const updatedBet = {
    ...betslip.value[actualIndex],
    stake: stakeValue,
  };
  betslipStore.betslip[actualIndex] = updatedBet;
  betslipStore.saveToStorage();
  
  // Save the stake as the last used stake
  updateLastStake(stakeValue);
};

const formatMarketDescription = (
  description: string,
  specifier: string | null
) => {
  if (description && description.includes("{value}") && specifier) {
    return description.replace("{value}", specifier);
  }
  return description;
};

const formatBetLabel = (
  label: string,
  config: { specifier_value?: string }
) => {
  if (label && label.includes("{value}") && config?.specifier_value) {
    return label.replace("{value}", config.specifier_value);
  }
  return label;
};

const formatDate = (date: string | undefined | null) => {
  if (!date) return "";
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const buildBackendPayload = () => {
  const betsPayload = bets.value.map((bet) => {
    // Simplified: always single outcome bets
    return {
      game_id: bet.game_id,
      market_id: bet.market_id,
      market_group_id: Number(bet.market_group_id),
      draws: Array.isArray(bet.draws) ? bet.draws : [],
      numbers: bet.numbers,
      picks: bet.picks,
      stake: bet.stake,
      outcome_id: Number(bet.outcome_id),
    };
  });
  return {
    bets: betsPayload,
    total_bet_amount: totalStake.value,
    ...(currentBookingCode.value ? { booking_code: currentBookingCode.value } : {}),
  };
};

const router = useRouter();
const route = useRoute();
const lastBetReference = ref<string | null>(null);

const handleBetButtonClick = () => {
  if (!isAuthenticated.value) {
    console.log('Sending login request from handleBetButtonClick');
    iframeCommunication.sendLoginRequest('bet-placement-required');
    return;
  }
  
  // User is authenticated, proceed with placing bet
  placeBet();
};

const placeBet = async () => {
  if (isProcessingBet.value || isEmpty.value) return;

  // Check for expired draws and show options if found
  if (!handleExpiredBets()) {
    return; // Don't proceed if there are expired bets
  }

  // Validate bet count against payload_max before submitting
  const gameStore = useGameStore();
  const maxBets = gameStore.getBetSettings?.payload_max || 2000;
  if (bets.value.length > maxBets) {
    showToast({
      title: "Maximum Bets Exceeded",
      message: `You have ${bets.value.length} bets but the maximum allowed is ${maxBets}. Please remove some bets.`,
      type: "error",
      duration: 4000,
    });
    return;
  }

  // Validate outcome_id is required for all bets
  const invalidBets = bets.value.filter(
    (bet) => !bet.outcome_id || bet.outcome_id === ""
  );
  if (invalidBets.length > 0) {
    // Reset success message if showing
    showSuccess.value = false;
    showToast({
      title: "Invalid Bet Configuration",
      message:
        "Some bets are missing required outcome information. Please refresh and try again.",
      type: "error",
      duration: 4000,
    });
    return;
  }

  isProcessingBet.value = true;

  try {
    const payload = buildBackendPayload();
    const result = await transactionsStore.createBet(payload);
    if (result.status === "success") {
      lastBetReference.value = result.bets_processed.reference;
      showSuccess.value = true;
      showToast({
        title: "Success! 🎉",
        message: "Your bet has been placed successfully!",
        type: "success",
        duration: 3000,
      });
      const betSummary = {
        reference: result.bets_processed.reference,
        betsPlaced: bets.value.length,
        totalStake: totalStake.value,
        potentialReturns: totalReturns.value,
        timestamp: new Date().toISOString(),
      };
      iframeCommunication.sendMessage("bet-placed", betSummary);
      emit("clearedAfterSubmission");
      currentBookingCode.value = null;
      clearBetslip(false);
      revealSuccessState();
      try {
        await transactionsStore.fetchTransactions();
      } catch (error) {
        console.error('Error refreshing wagers history:', error);
      }
      
      // Remove success message after 3 seconds
      setTimeout(() => {
        showSuccess.value = false;
      }, 3000);
      
      // Close modal after 5 seconds
      setTimeout(() => {
        emit("close");
      }, 5000);
    } else {
      throw new Error("Failed to place bet");
    }
  } catch (error: unknown) {
    // Reset success message if showing
    showSuccess.value = false;
    
    // Use the enhanced error message from the global interceptor
    const errorMessage =
      (error as { userMessage?: string })?.userMessage ||
      "Failed to place bet. Please try again.";

    // Set inline error message above BET NOW button
    inlineError.value = errorMessage;

    showToast({
      title: "Error",
      message: errorMessage,
      type: "error",
      duration: 5000,
    });

    // Clear inline error after 5 seconds
    setTimeout(() => {
      inlineError.value = null;
    }, 5000);
  } finally {
    isProcessingBet.value = false;
  }
};

const viewLastBet = async () => {
  if (!lastBetReference.value) {
    return;
  }

  try {
    isLoadingLastBet.value = true;
    const reference = lastBetReference.value;
    await transactionsStore.fetchTransactionDetail(reference);
    isLastBetModalOpen.value = true;
  } catch (error) {
    showToast({
      title: "Error",
      message: "Failed to load last bet details. Please try again.",
      type: "error",
      duration: 4000,
    });
  } finally {
    isLoadingLastBet.value = false;
  }
};

const viewWagers = () => {
  emit("close");
  nextTick(() => {
    router.push({ path: "/wagers", query: route.query });
  });
};

const handleBookBet = async () => {
  if (!isAuthenticated.value || isEmpty.value || isProcessingBookBet.value) return;
  if (!handleExpiredBets()) return;
  const invalidBets = bets.value.filter((b) => !b.outcome_id || b.outcome_id === "");
  if (invalidBets.length > 0) {
    showToast({ title: "Invalid bet", message: "Some bets are missing outcome information.", type: "error", duration: 4000 });
    return;
  }
  isProcessingBookBet.value = true;
  try {
    const currentBets = [...bets.value];
    const payload = buildBackendPayload();
    const result = await transactionsStore.bookBet(payload);
    if (result?.status === "success") {
      bookBetSuccessData.value = {
        booking_code: result.booking_code || "",
        barcode: result.barcode,
        expires_at: result.expires_at,
        total_bet_amount: result.total_bet_amount,
        bets: currentBets,
      };
      emit("clearedAfterSubmission");
      clearBetslip(false);
      revealSuccessState();
      showToast({ title: "Bet booked", message: result.message || "Save the code or scan the barcode to load later.", type: "success", duration: 3000 });
    } else {
      throw new Error(result?.message || "Failed to book bet");
    }
  } catch (err: unknown) {
    const msg = (err as { userMessage?: string })?.userMessage || (err as Error)?.message || "Failed to book bet";
    showToast({ title: "Error", message: msg, type: "error", duration: 5000 });
  } finally {
    isProcessingBookBet.value = false;
  }
};

const loadBookedBet = async () => {
  const code = bookingCodeInput.value?.trim();
  if (!code || !isAuthenticated.value || isLoadingBooking.value) return;
  isLoadingBooking.value = true;
  bookingMessage.value = "";
  bookingErrorMessage.value = "";
  try {
    const result = await transactionsStore.loadBookedBet(code);
    if (result?.status === "success" && result?.bet_data?.bets?.length) {
      currentBookingCode.value = result.booking_code || null;
      storeClearBetslip();
      const betsToAdd = result.bet_data.bets as Array<{
        game_id: number;
        market_id: number;
        market_group_id?: number;
        game_name?: string;
        market_name?: string;
        draws?: Array<{ draw_date: string; cutoff: string; event_id: number }>;
        numbers?: number[][];
        stake?: number;
        outcome_id?: string | number;
        odds?: number | string;
        picks?: number;
      }>;
      for (const b of betsToAdd) {
        const item: BetslipItem = {
          market_id: b.market_id,
          market_group_id: b.market_group_id ?? 0,
          game_id: b.game_id,
          game_name: b.game_name,
          draws: Array.isArray(b.draws) ? b.draws : [],
          numbers: Array.isArray(b.numbers) ? b.numbers : [[], []],
          stake: Number(b.stake) || 0,
          outcome_id: String(b.outcome_id ?? ""),
          market_name: b.market_name,
          odds: typeof b.odds === "string" ? parseFloat(b.odds) : b.odds,
          picks: b.picks,
        };
        betslipStore.addBet(item);
      }
      bookingCodeInput.value = "";
      bookingMessage.value = result.draws_updated
        ? "Booked bet loaded. Draw date was updated to the next available. Review and place bet."
        : "Booked bet loaded. Review and place your bet.";
      setTimeout(() => { bookingMessage.value = ""; }, 5000);
    } else {
      bookingErrorMessage.value = result?.message || "No bet data returned.";
      setTimeout(() => { bookingErrorMessage.value = ""; }, 5000);
    }
  } catch (err: unknown) {
    const msg = (err as { userMessage?: string })?.userMessage || (err as Error)?.message || "Failed to load booked bet";
    bookingErrorMessage.value = msg;
    setTimeout(() => { bookingErrorMessage.value = ""; }, 5000);
  } finally {
    isLoadingBooking.value = false;
  }
};

const downloadBookedBarcode = () => {
  const d = bookBetSuccessData.value;
  if (!d) return;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Thermal receipt style dimensions
  const width = 400;
  const padding = 20;
  const lineHeight = 20;
  let currentY = 40;

  // Pre-calculate height
  let estimatedHeight = 250; // Header and Footer
  if (d.bets) {
    d.bets.forEach((bet) => {
      estimatedHeight += 80; // Basic bet info
      if (bet.numbers && hasSelectedNumbers(bet.numbers)) {
        estimatedHeight += 30; // Numbers row
      }
      if (Array.isArray(bet.draws)) {
        estimatedHeight += 20 * Math.ceil(bet.draws.length / 2); // Draws
      }
    });
  }
  if (d.barcode) estimatedHeight += 120;

  canvas.width = width;
  canvas.height = estimatedHeight;

  // Background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, estimatedHeight);

  // Header
  ctx.fillStyle = "#000000";
  ctx.font = "bold 20px Arial";
  ctx.textAlign = "center";
  ctx.fillText("BOOKING RECEIPT", width / 2, currentY);
  currentY += 30;

  ctx.font = "14px Arial";
  ctx.fillText(`Booking Code: ${d.booking_code}`, width / 2, currentY);
  currentY += 25;

  ctx.font = "12px Arial";
  ctx.fillText(`Date: ${new Date().toLocaleString()}`, width / 2, currentY);
  currentY += 20;

  // Separator
  ctx.beginPath();
  ctx.moveTo(padding, currentY);
  ctx.lineTo(width - padding, currentY);
  ctx.stroke();
  currentY += 20;

  // Bets
  if (d.bets) {
    d.bets.forEach((bet, index) => {
      ctx.textAlign = "left";
      ctx.font = "bold 14px Arial";
      ctx.fillText(`${index + 1}. ${bet.game_name || "Unknown Game"}`, padding, currentY);
      currentY += lineHeight;

      ctx.font = "12px Arial";
      ctx.fillText(`Market: ${bet.market_name || ""}`, padding + 10, currentY);
      currentY += lineHeight;

      if (bet.numbers && hasSelectedNumbers(bet.numbers)) {
        const mainNumbers = (bet.numbers[0] || []).join(", ");
        const bonusNumbers = (bet.numbers[1] || []).join(", ");
        let numbersText = `Numbers: ${mainNumbers}`;
        if (bonusNumbers) numbersText += ` + Bonus: ${bonusNumbers}`;
        
        ctx.fillText(numbersText, padding + 10, currentY);
        currentY += lineHeight;
      }

      if (Array.isArray(bet.draws) && bet.draws.length > 0) {
        const drawDates = bet.draws.map(dw => formatDate(dw.draw_date)).join(", ");
        // Wrap text if too long
        const maxWidth = width - (padding * 2) - 20;
        let words = drawDates.split(' ');
        let line = 'Draws: ';
        for(let n = 0; words[n]; n++) {
          let testLine = line + words[n] + ' ';
          let metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && n > 0) {
            ctx.fillText(line, padding + 10, currentY);
            line = words[n] + ' ';
            currentY += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line, padding + 10, currentY);
        currentY += lineHeight;
      }

      ctx.textAlign = "right";
      ctx.fillText(`Stake: ${formatCurrency(bet.stake || 0, unref(displayCurrency))}`, width - padding, currentY);
      currentY += 10;

      // Bet separator
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(padding + 10, currentY);
      ctx.lineTo(width - padding, currentY);
      ctx.stroke();
      ctx.setLineDash([]);
      currentY += 20;
    });
  }

  // Footer
  ctx.textAlign = "center";
  ctx.font = "bold 16px Arial";
  ctx.fillText(`Total Amount: ${formatCurrency(d.total_bet_amount || 0, unref(displayCurrency))}`, width / 2, currentY);
  currentY += 25;

  if (d.expires_at) {
    ctx.font = "12px Arial";
    ctx.fillText(`Expires: ${formatDate(d.expires_at)}`, width / 2, currentY);
    currentY += 20;
  }

  // Barcode
  if (d.barcode) {
    const img = new Image();
    img.onload = () => {
      // Scale barcode to fit width
      const barcodeWidth = 300;
      const barcodeHeight = 80;
      ctx.drawImage(img, (width - barcodeWidth) / 2, currentY, barcodeWidth, barcodeHeight);
      
      // Final download
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `bet-booking-${d.booking_code}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    img.src = `data:image/png;base64,${d.barcode}`;
  } else {
    // No barcode, download immediately
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `bet-booking-${d.booking_code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const removeDraw = (displayBetIndex: number, drawIndex: number) => {
  if (!betslip.value) return;
  
  const actualBetIndex = displayBetIndex;
  const bet = betslip.value[actualBetIndex];
  
  if (!bet || !Array.isArray(bet.draws) || bet.draws.length === 0) return;
  
  if (bet.draws.length === 1) {
    showToast({
      title: "Cannot Remove Last Draw",
      message: "Each bet must have at least one draw selected.",
      type: "info",
      duration: 3000,
    });
    return;
  }
  
  const removedDraw = bet.draws[drawIndex];
  const updatedDraws = bet.draws.filter((_, idx) => idx !== drawIndex);
  
  const updatedBet = {
    ...bet,
    draws: updatedDraws,
    drawCount: updatedDraws.length,
  };
  
  betslipStore.betslip[actualBetIndex] = updatedBet;
  betslipStore.saveToStorage();
  
  emit("selectDraws", {
    betIndex: actualBetIndex,
    drawCount: updatedDraws.length,
    selectedDraws: updatedDraws,
    gameId: bet.game_id,
  });
};

const toggleDrawDropdown = (index: number) => {
  if (openDrawDropdownIndex.value === index) {
    openDrawDropdownIndex.value = null;
  } else {
    openDrawDropdownIndex.value = index;
  }
};

const isDrawSelected = (draw: { draw_date: string; cutoff: string; event_id: number }, bet: BetslipItem): boolean => {
  if (!Array.isArray(bet.draws) || bet.draws.length === 0) return false;
  
  return bet.draws.some((selectedDraw) => {
    if (selectedDraw.event_id && draw.event_id) {
      return selectedDraw.event_id === draw.event_id;
    }
    return selectedDraw.draw_date === draw.draw_date;
  });
};

const toggleDrawSelection = (
  betIndex: number,
  draw: { draw_date: string; cutoff: string; event_id: number },
  event: Event
) => {
  if (!betslip.value) return;
  
  const bet = betslip.value[betIndex];
  if (!bet) return;
  
  const isChecked = (event.target as HTMLInputElement).checked;
  const currentDraws = Array.isArray(bet.draws) ? [...bet.draws] : [];
  
  if (isChecked) {
    // Add draw if not already selected
    const isAlreadySelected = currentDraws.some((selectedDraw) => {
      if (selectedDraw.event_id && draw.event_id) {
        return selectedDraw.event_id === draw.event_id;
      }
      return selectedDraw.draw_date === draw.draw_date;
    });
    
    if (!isAlreadySelected) {
      currentDraws.push(draw);
    }
  } else {
    // Remove draw, but ensure at least one remains
    if (currentDraws.length === 1) {
      showToast({
        title: "Cannot Remove Last Draw",
        message: "Each bet must have at least one draw selected.",
        type: "info",
        duration: 3000,
      });
      // Re-check the checkbox
      (event.target as HTMLInputElement).checked = true;
      return;
    }
    
    const updatedDraws = currentDraws.filter((selectedDraw) => {
      if (selectedDraw.event_id && draw.event_id) {
        return selectedDraw.event_id !== draw.event_id;
      }
      return selectedDraw.draw_date !== draw.draw_date;
    });
    
    currentDraws.length = 0;
    currentDraws.push(...updatedDraws);
  }
  
  const updatedBet = {
    ...bet,
    draws: currentDraws,
    drawCount: currentDraws.length,
  };
  
  betslipStore.betslip[betIndex] = updatedBet;
  betslipStore.saveToStorage();
  
  emit("selectDraws", {
    betIndex: betIndex,
    drawCount: currentDraws.length,
    selectedDraws: currentDraws,
    gameId: bet.game_id,
  });
};

const isDrawExpired = (draw: string | { draw_date: string; cutoff: string }) => {
  // Accepts both string and object with cutoff time
  let cutoffTime: Date;
  
  if (typeof draw === "string") {
    cutoffTime = new Date(draw);
  } else {
    // Use cutoff time if available, otherwise fall back to draw_date
    cutoffTime = new Date(draw?.cutoff || draw?.draw_date);
  }

  // Check if the date is valid
  if (isNaN(cutoffTime.getTime())) {
    return false; // Invalid date, don't consider expired
  }

  // Check if cutoff time has passed (no buffer needed, be strict)
  const now = new Date();
  return cutoffTime.getTime() <= now.getTime();
};

const validateDraws = () => {
  const expired: { [betIndex: number]: number[] } = {};
  bets.value.forEach((bet, betIndex) => {
    if (Array.isArray(bet.draws)) {
      bet.draws.forEach((draw, drawIndex) => {
        if (isDrawExpired(draw)) {
          if (!expired[betIndex]) expired[betIndex] = [];
          expired[betIndex].push(drawIndex);
        }
      });
    }
  });
  
  const hasExpired = Object.keys(expired).length > 0;
  return !hasExpired;
};

// Check for expired draws and collect them for user action
const checkForExpiredDraws = () => {
  const expired: Array<{ betIndex: number; drawIndex: number; draw: { draw_date: string; cutoff: string; event_id: number } }> = [];
  bets.value.forEach((bet, betIndex) => {
    if (Array.isArray(bet.draws)) {
      bet.draws.forEach((draw, drawIndex) => {
        if (isDrawExpired(draw)) {
          expired.push({ betIndex, drawIndex, draw });
        }
      });
    }
  });
  return expired;
};

// Handle expired bet options
const handleExpiredBets = () => {
  const expired = checkForExpiredDraws();
  
  if (expired.length > 0) {
    expiredBets.value = expired;
    showExpiredBetOptions.value = true;
    return false; // Prevent bet submission
  }
  return true; // Allow bet submission
};

// Remove expired bets from betslip
const removeExpiredBets = () => {
  // Sort by betIndex in descending order to avoid index shifting issues
  const sortedExpired = [...expiredBets.value].sort((a, b) => b.betIndex - a.betIndex);
  
  sortedExpired.forEach(({ betIndex, drawIndex }) => {
    const bet = bets.value[betIndex];
    if (bet && Array.isArray(bet.draws)) {
      // Remove the expired draw
      bet.draws.splice(drawIndex, 1);
      
      // If no draws left, remove the entire bet
      if (bet.draws.length === 0) {
        removeBetAtIndex(betIndex);
      }
    }
  });
  
  // Hide the expired bet options and clear highlighting
  showExpiredBetOptions.value = false;
  expiredBets.value = [];
};

// Move expired bets to next available draw
const moveToNextDraw = () => {
  let hasMovedAny = false;
  let failedMoves = 0;
  
  // Filter to only process single draw bets
  const singleDrawExpiredBets = expiredBets.value.filter(({ betIndex }) => {
    const bet = bets.value[betIndex];
    return bet && Array.isArray(bet.draws) && bet.draws.length === 1;
  });
  
  if (singleDrawExpiredBets.length === 0) {
    showToast({
      title: "Cannot Move Multi-Draw Bets",
      message: "Only single draw bets can be moved to the next draw. Please remove multi-draw bets manually.",
      type: "error",
      duration: 4000,
    });
    return;
  }
  
  // Group expired bets by game_id to handle each game's draws separately
  const expiredBetsByGame = singleDrawExpiredBets.reduce((acc, { betIndex, drawIndex, draw }) => {
    const bet = bets.value[betIndex];
    if (bet && bet.game_id) {
      if (!acc[bet.game_id]) {
        acc[bet.game_id] = [];
      }
      acc[bet.game_id].push({ betIndex, drawIndex, draw, bet });
    }
    return acc;
  }, {} as Record<string | number, Array<{ betIndex: number; drawIndex: number; draw: { draw_date: string; cutoff: string; event_id: number }; bet: BetslipItem }>>);
  
  // Process each game's expired bets separately
  Object.entries(expiredBetsByGame).forEach(([gameId, gameExpiredBets]) => {
    // Get the first bet for this game to access its allAvailableDraws
    const firstBet = gameExpiredBets[0].bet;
    const availableDraws = firstBet.allAvailableDraws || [];
    
    if (availableDraws.length === 0) {
      failedMoves += gameExpiredBets.length;
      return;
    }
    
    // Filter out expired draws and get the next available draw for this specific game
    const validDraws = availableDraws.filter(draw => !isDrawExpired(draw));
    
    if (validDraws.length === 0) {
      failedMoves += gameExpiredBets.length;
      return;
    }
    
    const nextDraw = validDraws[0];
    
    // Update all expired draws for this game
    gameExpiredBets.forEach(({ betIndex, drawIndex }) => {
      const bet = bets.value[betIndex];
      if (bet && Array.isArray(bet.draws)) {
        // Update the bet in the betslip store
        const updatedDraws = bet.draws.map((draw, idx) => 
          idx === drawIndex 
            ? {
                draw_date: nextDraw.draw_date,
                cutoff: nextDraw.cutoff,
                event_id: nextDraw.event_id,
              }
            : draw
        );
        
        betslipStore.betslip[betIndex] = {
          ...bet,
          draws: updatedDraws,
        };
        hasMovedAny = true;
      }
    });
  });
  
  // Save the updated betslip to storage
  if (hasMovedAny) {
    betslipStore.saveToStorage();
  }
  
  // Hide the expired bet options and clear highlighting
  showExpiredBetOptions.value = false;
  expiredBets.value = [];
  
  // Show appropriate message based on results
  if (hasMovedAny && failedMoves === 0) {
    showToast({
      title: "Moved to Next Draw",
      message: "Single draw bets have been moved to the next available draw for each game.",
      type: "success",
      duration: 3000,
    });
  } else if (hasMovedAny && failedMoves > 0) {
    showToast({
      title: "Partially Moved",
      message: `Some single draw bets were moved to next draw. ${failedMoves} bet(s) couldn't be moved (no next draw available).`,
      type: "info",
      duration: 4000,
    });
  } else {
    showToast({
      title: "No Next Draw",
      message: "No next draw is available for any of the expired single draw bets. Please remove them.",
      type: "error",
      duration: 3000,
    });
  }
};

// Check if a specific bet has expired draws
const hasExpiredDraws = (bet: { draws?: Array<{ draw_date: string; cutoff: string; event_id: number }> }) => {
  if (!Array.isArray(bet.draws)) return false;
  return bet.draws.some((draw: { draw_date: string; cutoff: string; event_id: number }) => isDrawExpired(draw));
};

// Check if any of the expired bets have available next draws
const hasAnyGameWithNextDraw = computed(() => {
  if (expiredBets.value.length === 0) return false;
  
  // Check if any expired bet has only 1 draw (single draw bets can be moved)
  const hasSingleDrawBets = expiredBets.value.some(({ betIndex }) => {
    const bet = bets.value[betIndex];
    return bet && Array.isArray(bet.draws) && bet.draws.length === 1;
  });
  
  if (!hasSingleDrawBets) return false; // Only single draw bets can be moved
  
  // Get unique game IDs from expired bets
  const gameIds = new Set();
  expiredBets.value.forEach(({ betIndex }) => {
    const bet = bets.value[betIndex];
    if (bet && bet.game_id) {
      gameIds.add(bet.game_id);
    }
  });
  
  // Check if any game has available draws
  return Array.from(gameIds).some(gameId => {
    const betForGame = bets.value.find(bet => bet.game_id === gameId);
    return betForGame && betForGame.allAvailableDraws && betForGame.allAvailableDraws.length > 0;
  });
});

// Automatically clean up expired draws
const cleanupExpiredDraws = () => {
  if (!betslip.value || betslip.value.length === 0) return;

  // Don't clean up if there are expired draws - let user see the error and choose new draws
  if (!validateDraws()) {
    return;
  }

  let hasChanges = false;
  const currentGameId = gameStore.getGameDetail?.id;
  const betsToRemove: number[] = [];

  betslip.value.forEach((bet, betIndex) => {
    if (bet.game_id === currentGameId && Array.isArray(bet.draws)) {
      const validDraws = bet.draws.filter((draw) => !isDrawExpired(draw));

      if (validDraws.length === 0) {
        // No valid draws left, mark bet for removal
        betsToRemove.push(betIndex);
        hasChanges = true;
      } else if (validDraws.length !== bet.draws.length) {
        // Some draws were expired, update the bet
        betslipStore.betslip[betIndex] = {
          ...bet,
          draws: validDraws,
          drawCount: validDraws.length,
        };
        betslipStore.saveToStorage();
        hasChanges = true;
      }
    }
  });

  // Remove bets with no valid draws (in reverse order to maintain indices)
  betsToRemove.reverse().forEach((betIndex) => {
    removeBet(betIndex);
  });

  if (hasChanges) {
    // Only emit drawsChanged if there are still bets in the betslip
    if (betslip.value.length > 0) {
      // Emit event to update DetailView with the cleaned draws
      const allDraws: Array<{
        draw_date: string;
        cutoff: string;
        event_id: number;
      }> = [];
      betslip.value
        .filter((bet) => bet.game_id === currentGameId)
        .forEach((bet) => {
          if (Array.isArray(bet.draws)) {
            allDraws.push(...bet.draws);
          }
        });
      emit("drawsChanged", allDraws);
    }
  }
};

// Start cleanup timer on mount
onMounted(() => {
  // Detect mobile device
  const checkMobile = () => {
    isMobile.value = window.innerWidth <= 768;
  };
  
  checkMobile();
  window.addEventListener('resize', checkMobile);

  // Run cleanup every 30 seconds to remove expired draws
  cleanupTimer.value = window.setInterval(() => {
    cleanupExpiredDraws();
  }, 30000);

  // Close dropdown when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.draw-dropdown-wrapper')) {
      openDrawDropdownIndex.value = null;
    }
  };
  
  clickOutsideHandler.value = handleClickOutside;
  document.addEventListener('click', handleClickOutside);
});

// Clean up timer on unmount
onUnmounted(() => {
  if (cleanupTimer.value) {
    clearInterval(cleanupTimer.value);
    cleanupTimer.value = null;
  }
  
  // Remove resize listener
  const checkMobile = () => {
    isMobile.value = window.innerWidth <= 768;
  };
  window.removeEventListener('resize', checkMobile);
  
  // Remove click outside handler
  if (clickOutsideHandler.value) {
    document.removeEventListener('click', clickOutsideHandler.value);
    clickOutsideHandler.value = null;
  }
});
</script>

<script lang="ts">
export default {
  name: "BetSlip",
};
</script>

<style scoped>
.success-message {
  background: var(--bg-success);
  padding: 24px;
  margin-bottom: 16px;
  animation: slideDown 0.3s ease-out;
}

.success-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.success-icon {
  font-size: 32px;
}

.success-text h3 {
  color: var(--text-white);
  margin: 0 0 4px 0;
  font-size: 18px;
}

.success-text p {
  color: var(--text-white);
  margin: 0;
  font-size: 14px;
}

.success-text {
  flex: 1;
}

.view-bet-button {
  background: var(--bg-success);
  color: var(--text-white);
  padding: 12px 24px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  margin-bottom: 8px;
  border: none !important;
}

.view-bet-button:last-child {
  margin-bottom: 0;
}

.view-bet-button:disabled {
  background: var(--bg-secondary);
  cursor: not-allowed;
  opacity: 0.5;
}

.view-bet-button:hover:not(:disabled) {
  background: var(--bg-success-hover);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.place-bet {
  width: 100%;
  padding: 12px;
  background-color: var(--bg-bet-button, var(--bg-success));
  color: var(--bg-bet-color, var(--text-white));
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.place-bet:hover {
  background: var(--bg-bet-button, var(--bg-success-hover));
}

.place-bet:disabled {
  background: var(--lottery-card-gradient);
  cursor: not-allowed;
  opacity: 0.5;
}

.place-bet.unauthenticated {
  background: var(--bg-danger) !important;
  color: var(--text-white) !important;
  opacity: 1 !important;
  cursor: pointer !important;
  pointer-events: auto !important;
}

.place-bet.unauthenticated:hover {
  background: var(--bg-danger-hover, var(--bg-danger)) !important;
  opacity: 1 !important;
}

.place-bet.processing {
  position: relative;
  pointer-events: none;
  background: var(--lottery-card-gradient);
}

.place-bet.processing::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border: 2px solid var(--text-white);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.close-betslip-button {
  width: 100%;
  padding: 12px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;
  margin-bottom: 250px;
}

.close-betslip-button:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-color);
}

@media (min-width: 769px) {
  .close-betslip-button {
    display: none;
  }
}
.betslip {
  background-color: var(--bg-bet-slip, var(--bg-secondary));
  border-radius: 0;
  padding: 0;
  color: var(--text-primary);
  width: 100%;
  max-width: 100%;
  min-width: 300px;
  box-sizing: border-box;
  position: relative;
  overflow: visible;
}

html:not(.theme-dark) .betslip.betslip-body-surface {
  background-color: #ffffff;
}

html.theme-dark .betslip.betslip-body-surface {
  background-color: var(--betslip-body-surface-bg, #2C2C2E);
}

.betslip-header.header-bar-surface {
  background: var(--header-bar-surface-bg, #2B364C);
}

.betslip-header.header-bar-surface h3 {
  color: var(--text-on-card-contrast, var(--text-white));
}

.betslip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 8px;
  border-radius: 0;
  width: 100%;
  gap: 16px;
  box-sizing: border-box;
}

.betslip-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  flex: 1;
  min-width: 0;
}

.bet-count {
  background-color: var(--bg-bet-count, var(--bg-dark-card));
  color: var(--text-bet-count, var(--text-white));
  border-radius: 50%;
  width: 32px;
  height: 32px;
  min-width: 32px;
  max-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  margin-left: 8px;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: var(--text-light);
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.empty-state .empty-state-login {
  width: auto;
  min-width: 200px;
  padding: 12px 24px;
}

.auth-prompt {
  text-align: center;
  padding: 1rem 2rem 2rem;
}

.betslip-content {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  overflow-x: visible;
  width: 100%;
  box-sizing: border-box;
}

.bet-item {
  background-color: var(--bg-bet-slip-item, var(--border-light)) !important;
  overflow: visible;
  border: 1px solid var(--border-color) !important;
  position: relative;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
}

.bet-header {
  padding: 12px 16px;
  background-color: var(--bg-bet-slip-item, var(--border-light));
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  width: 100%;
  box-sizing: border-box;
  margin-top: 5px;
}

.bet-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: var(--text-primary);
  width: 100%;
  flex: 1;
}

.game-name {
  font-weight: 600;
  color: var(--text-primary);
}

.game-date {
  color: var(--text-secondary);
  font-size: 12px;
  margin-left: 8px;
}

.close-button {
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.bet-numbers {
  padding: 0px 16px;
  background: var(--bg-bet-slip-item, var(--border-light));
  width: 100%;
  box-sizing: border-box;
}

.numbers-label {
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  width: 100%;
  box-sizing: border-box;
}

.market-info {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.market-desc {
  font-size: 0.8rem;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  line-height: 1.3;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bet-label {
  font-weight: 500;
  color: var(--text-bet-label,var(--bg-danger));
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.number-circles {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.number-circle {
  background-color: transparent;
  border: 2px solid var(--text-primary);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
}

.number-circle.bonus {
  background-color: transparent;
  border: 2px solid var(--bg-danger);
  color: var(--bg-danger);
}

.bet-details {
  padding: 10px 16px;
  background: var(--border-light);
  width: 100%;
  box-sizing: border-box;
}

.bet-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-light);
  width: 100%;
  box-sizing: border-box;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
  flex: 1;
}

.control-group span {
  font-size: 0.9rem;
  color: var(--text-primary);
  white-space: nowrap;
}

.stake-input {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.currency {
  color: var(--text-primary);
  font-size: 0.9rem;
}

.stake-draw-grid {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.stake-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.draw-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.selected-draws-display {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.draws-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.draw-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: var(--bg-filter-select, var(--lottery-card-gradient));
  border: 1px solid var(--border-color);
  border-radius: 20px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.4;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.draw-badge:hover {
  background: var(--lottery-card-gradient);
  border-color: var(--border-color);
  color: var(--text-on-card-contrast, var(--text-white));
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.25);
}

.no-draws-selected {
  color: var(--text-secondary);
  font-size: 13px;
  font-style: italic;
  padding: 4px 0;
}

.field-label {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.stake-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--bg-filter-select, var(--lottery-card-gradient));
  border: 1px solid var(--bg-filter-select-focus, white);
  border-radius: 6px;
  overflow: hidden;
  width: 89px;
  height: 32px;
}

.stake-input {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--text-primary);
  padding: 6px 8px 6px 24px;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  text-align: center;
  height: 100%;
}

.stake-input::placeholder {
  color: var(--text-secondary);
  text-align: center;
}

.currency-symbol {
  position: absolute;
  left: 8px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
  z-index: 1;
}

.selected-draws-container {
  width: 100%;
  min-height: 32px;
  max-height: 120px;
  overflow-y: auto;
  background: var(--lottery-card-gradient);
  border-radius: 6px;
  padding: 4px 8px;
  border: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.selected-draws-container:hover {
  border-color: var(--border-color);
}

.selected-draws-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.selected-draw-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.2s ease;
  border: 1px solid var(--bg-primary);
}

.selected-draw-item:hover {
  border-color: var(--border-color);
}

.selected-draw-item.expired {
  opacity: 0.8;
  border-color: var(--bg-danger);
}

.draw-date-text {
  flex: 1;
  color: var(--text-white);
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.remove-draw-btn {
  background: transparent;
  border: none;
  color: var(--text-white);
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 8px;
  line-height: 1;
}

.remove-draw-btn:hover:not(:disabled) {
  background: var(--bg-danger);
  color: var(--text-white);
  transform: scale(1.15);
}

.remove-draw-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.no-draws-message {
  color: var(--text-secondary);
  font-size: 12px;
  font-style: italic;
  padding: 8px;
  text-align: center;
}

/* Draw Dropdown Styles */
.draw-dropdown-wrapper {
  position: relative;
  width: 100%;
}

.draw-dropdown-trigger {
  width: 100%;
  min-height: 32px;
  background: var(--lottery-card-gradient);
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 2px 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-white);
  font-size: 12px;
  font-weight: 500;
}

.draw-dropdown-trigger:hover:not(.active) {
  background: var(--dropdown-trigger-hover);
  border-color: var(--border-color);
}

.draw-dropdown-trigger.active {
  border-color: var(--border-color);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.draw-count-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draw-dropdown-arrow {
  margin-left: 8px;
  font-size: 10px;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.draw-dropdown-trigger.active .draw-dropdown-arrow {
  transform: rotate(180deg);
}

.draw-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--lottery-card-gradient);
  border: 1px solid var(--border-color);
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.draw-dropdown-list {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}

.draw-dropdown-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  margin: 2px 0px;
}

.draw-dropdown-item:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.08);
}

.draw-dropdown-item:active:not(.disabled) {
  background: rgba(255, 255, 255, 0.12);
}

.draw-dropdown-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.draw-dropdown-item:has(.draw-checkbox:checked) {
  background: rgba(34, 197, 94, 0.1);
}

.draw-dropdown-item:has(.draw-checkbox:checked):hover:not(.disabled) {
  background: rgba(34, 197, 94, 0.15);
}

.draw-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: var(--bg-success);
  transition: all 0.2s ease;
}

.draw-checkbox:hover:not(:disabled) {
  transform: scale(1.1);
}

.draw-checkbox:disabled {
  cursor: not-allowed;
}

.draw-label-text {
  flex: 1;
  color: var(--text-white);
  font-size: 12px;
  font-weight: 500;
}

.draw-expired-badge {
  font-size: 10px;
  color: var(--bg-danger);
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(220, 38, 38, 0.1);
  flex-shrink: 0;
}

.draw-dropdown-item.expired .draw-label-text {
  opacity: 0.7;
}

/* Responsive design */
@media (max-width: 768px) {
  .betslip {
    max-height: none !important;
    overflow-y: auto !important;
    overflow-x: visible !important;
    height: auto !important;
    min-width: 280px;
  }

  .betslip-content {
    max-height: none !important;
    overflow-y: visible !important;
    overflow-x: visible !important;
  }

  .stake-draw-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stake-section {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .draw-section {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  
  .draws-list {
    justify-content: flex-end;
  }
  
  .selected-draws-display {
    justify-content: flex-end;
  }
  
  .selected-draws-display {
    flex: 1;
    overflow: visible;
  }
  
  .draws-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 100%;
  }
  
  .draw-badge {
    font-size: 11px;
    padding: 3px 8px;
  }
  
  .no-draws-selected {
    font-size: 12px;
  }

  .stake-input-container {
    width: 70%;
    margin-left: auto;
  }

  .selected-draws-container {
    width: 100%;
  }

  .draw-dropdown-wrapper {
    width: 100%;
  }

  .draw-dropdown-trigger {
    min-height: 36px;
    padding: 8px 12px;
    font-size: 13px;
  }

  .draw-dropdown-menu {
    max-height: 180px;
  }

  .draw-dropdown-item {
    padding: 10px 12px;
  }

  .draw-label-text {
    font-size: 13px;
  }

  .stake-input {
    font-size: 14px;
    padding: 10px 12px;
    width: 100%;
  }

  .field-label {
    font-size: 13px;
  }

  .place-bet {
    background-color: var(--bg-bet-button, var(--bg-success));
    color: var(--bg-bet-color, var(--text-white));
    border: none;
    border-radius: 8px;
    padding: 16px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    width: 100%;
    text-transform: uppercase;
    transition: background-color 0.2s;
    margin-bottom: 0;
  }

  .place-bet.unauthenticated {
    background: var(--bg-danger) !important;
    color: var(--text-white) !important;
    opacity: 1 !important;
    cursor: pointer !important;
    pointer-events: auto !important;
  }

  .place-bet.unauthenticated:hover {
    background: var(--bg-danger-hover, var(--bg-danger)) !important;
    opacity: 1 !important;
  }

  .selected-draws-container {
    max-height: 100px;
  }

  .selected-draw-item {
    padding: 6px 10px;
  }

  .draw-date-text {
    font-size: 13px;
  }

  .remove-draw-btn {
    width: 22px;
    height: 22px;
    font-size: 20px;
  }
}

.bet-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  font-size: 14px;
  padding: 2px 0;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}

.bet-row span {
  text-align: center;
}

.bet-row:last-child {
  margin-bottom: 0;
}

.win-amount {
  color: var(--bg-success);
  font-weight: 500;
}

.betslip-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0.5rem 1rem 1rem 1rem;
  background: var(--bg-bet-slip, var(--bg-secondary));
  border-radius: 0;
  overflow: visible;
  position: relative;
  z-index: 1;
}

html:not(.theme-dark) .betslip-body-surface .betslip-footer {
  background-color: #ffffff;
}

html.theme-dark .betslip-body-surface .betslip-footer {
  background-color: var(--betslip-body-surface-bg, #2C2C2E);
}

.clear-button-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.book-bet-button {
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  color: var(--text-primary);
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 24.86px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}
.book-bet-button:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-color);
}
.book-bet-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.betslip-load-booking {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-tertiary);
}
.booking-input-container {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
}
.booking-code-input {
  flex: 1;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
}
.booking-code-input::placeholder {
  color: var(--text-light);
}
.booking-search-btn {
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  background: var(--bg-button);
  color: var(--text-white);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}
.booking-search-btn:hover:not(:disabled) {
  background: var(--bg-button-hover);
}
.booking-search-btn:disabled {
  background: var(--bg-button-disabled);
  cursor: not-allowed;
  opacity: 0.8;
}
.booking-message {
  font-size: 12px;
  margin-top: 4px;
}
.booking-message.success { color: var(--bg-success); }
.booking-message.error { color: var(--error-color); }

.booked-bet-success-inline {
  padding: 12px 0;
}
.booked-success-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 12px 0;
}
.booked-bet-success-inline .booking-info {
  background: var(--bg-tertiary);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid var(--border-light);
}
.booked-bet-success-inline .booking-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 14px;
  color: var(--text-primary);
}
.booked-bet-success-inline .booking-row:last-child { margin-bottom: 0; }
.booked-bet-success-inline .booking-row label {
  color: var(--text-secondary);
}
.booked-bet-success-inline .code-value {
  font-family: monospace;
  font-weight: 600;
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid var(--border-light);
}
.booked-bet-success-inline .barcode-box {
  text-align: center;
  padding: 12px;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  margin-bottom: 12px;
  background: var(--bg-secondary);
}
.booked-bet-success-inline .barcode-img {
  max-width: 200px;
  max-height: 120px;
}
.booked-bet-success-inline .barcode-hint {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 8px;
}
.booked-bet-success-inline .booking-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}
.booked-bet-success-inline .download-barcode-btn,
.booked-bet-success-inline .done-btn {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}
.booked-bet-success-inline .download-barcode-btn {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-light);
}
.booked-bet-success-inline .download-barcode-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-color);
}
.booked-bet-success-inline .done-btn {
  background: var(--bg-button);
  color: var(--text-white);
  border: none;
}
.booked-bet-success-inline .done-btn:hover {
  background: var(--bg-button-hover);
}

.clear-button {
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  font-size: 12px;
  border-radius: 24.86px;
  position: relative;
}

.trash-icon {
  font-size: 16px;
  position: relative;
  margin-left: 10px;
}

.trash-icon::after {
  content: "1";
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--bg-danger);
  color: var(--text-white);
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.total-bet {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 4px 0;
  border-top: 1px solid var(--border-light);
  color: var(--text-primary);
}

.amount {
  font-weight: 500;
  color: var(--text-primary);
}


.bet-now-button:hover {
  background-color: var(--bg-success-hover);
}

/* Custom scrollbar for betslip-content */
.betslip-content::-webkit-scrollbar {
  width: 6px;
}

.betslip-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.betslip-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.betslip-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Medium screen responsive styles - Grid layout */
@media (max-width: 1024px) and (min-width: 769px) {
  .betslip {
    border-radius: 8px;
    padding: 0.75rem;
    max-height: 70vh;
    overflow: visible;
    min-width: 300px;
  }

  .betslip-content {
    max-height: calc(70vh - 120px);
    overflow-y: auto;
    overflow-x: visible;
  }

  .bet-item {
    margin-bottom: 0.75rem;
  }

  .bet-header {
    padding: 0.5rem 0.75rem;
  }

  .bet-numbers {
    padding: 0.75rem;
  }

  .bet-details {
    padding: 0.75rem;
  }

  .bet-controls {
    gap: 0.5rem;
  }

  .stake-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .stake-group > span {
    text-align: left;
    width: 100%;
  }

  .stake-input {
    width: 100%;
  }

  .betslip-footer {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border-color);
    margin-bottom: 250px;
  }

  .bet-now-button {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
}

.draw-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}
.draw-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: var(--bg-secondary);
  border-radius: 4px;
}
.remove-draw {
  background: none;
  border: none;
  color: var(--bg-danger);
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0;
  line-height: 1;
}
.stake-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}
.stake-group > span {
  flex: 1 1 0;
  text-align: center;
}

.plus-sign {
  color: var(--bg-danger);
  font-size: 16px;
  font-weight: 500;
  margin: 0 4px;
  align-self: center;
}

.expired {
  color: var(--error-color) !important;
  border: 1px solid var(--error-border);
}

.view-last-bet {
  background: transparent;
  padding: 0;
  border-radius: 8px;
  margin-bottom: 0;
  text-align: center;
}

.view-bet-button {
  background: var(--bg-success);
  color: var(--text-white);
  padding: 12px 24px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  border: none !important;
}

.view-bet-button:hover {
  background: var(--bg-success-hover);
}

/* Expired bet highlighting */
.bet-item.expired-bet {
  border: 2px solid var(--accent-color) !important;
  border-radius: 8px;
  position: relative;
  animation: pulseWarning 1.5s ease-in-out infinite;
}

.bet-item.expired-bet .bet-header,
.bet-item.expired-bet .bet-numbers,
.bet-item.expired-bet .bet-details,
.bet-item.expired-bet .bet-controls,
.bet-item.expired-bet .bet-row,
.bet-item.expired-bet .stake-draw-grid,
.bet-item.expired-bet .numbers-label,
.bet-item.expired-bet .market-info,
.bet-item.expired-bet .market-desc,
.bet-item.expired-bet .bet-label {
  background: transparent !important;
}

.bet-item.expired-bet::before {
  position: absolute;
  top: -8px;
  right: 8px;
  background: var(--bg-danger);
  color: var(--text-white);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  z-index: 10;
  animation: slideDown 0.3s ease-out;
}

@keyframes pulseWarning {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(255, 193, 7, 0.1);
  }
}

/* Expired bet options */
.expired-bet-options {
  background: var(--bg-warning);
  border: 1px solid var(--border-warning);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  animation: slideDown 0.3s ease-out;
}

.expired-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}

.warning-icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
}

.warning-text {
  color: var(--text-warning);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.expired-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.remove-expired-btn {
  background: var(--bg-danger);
  color: var(--text-white);
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-expired-btn:hover {
  background: var(--bg-danger-hover);
}

.move-to-next-btn {
  background: var(--bg-success);
  color: var(--text-white);
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.move-to-next-btn:hover {
  background: var(--bg-primary-hover);
}

/* Inline error message above BET NOW button */
.inline-error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-danger);
  color: var(--text-white);
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  animation: slideDown 0.3s ease-out;
}

.error-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.error-text {
  flex: 1;
  font-weight: 500;
}
</style>
